import React from 'react';
import { Area, AreaChart, CartesianAxis, Label, ReferenceLine, ResponsiveContainer, Tooltip, XAxis } from 'recharts';
import { useTranslation } from 'react-i18next';
import { Box, useTheme } from '@material-ui/core';
import { orderBy } from 'lodash/fp';
import { Card, CardContent, CardTitle, DownloadCsvIconButton } from '../../../../ui-kit';
import { GET_MONEY_FLOW_REPORT } from '../../graphql';
import { usePriceService } from '../../../../utils/hooks/priceService';
import { Placeholder } from '../Placeholder';
import { translate } from '../../../../utils';
import { useDateTimeService } from '../../../../utils/hooks/dateTimeService';
import { useUser } from '../../../../utils/hooks/auth';
import { useFromToContext } from '../../../shared';
import { DateFromTo, SalesChartComponent } from '../../../../generated/graphql';
import { CHART_PLACEHOLDER_HEIGHT } from '../constants';
import { generateChartData, } from './utils';

const text = {
    cash: translate('Cash'),
    creditCard: translate('PaymentCards'),
    total: translate('Total'),
    other: translate('Other'),
};
const Chart = () => {
    const { priceService } = usePriceService();
    const { dateTimeService } = useDateTimeService();
    const { user } = useUser();
    const { t } = useTranslation();
    const { dateFromTo } = useFromToContext();
    const theme = useTheme();

    let showHours = true;
    let diffMoreThanOneDay = true;

    if (dateFromTo) {
        const dif = dateTimeService.getDifFromDate(dateFromTo);
        diffMoreThanOneDay = dif.days > 1;
        showHours = dif.days > 0 && dif.days <= 2;
    }

    const formatChartTooltip = (value: string | number | Array<string | number>, name: string) => {
        const formattedName = t(...text[name as keyof typeof text]);
        const formttedValue = priceService.toLocaleString(value as number);
        return [formttedValue, formattedName];
    };

    const formatDate = (date: string | number) => {
        return `${dateTimeService.localizeDateWithWeekday(date, user.cultureCode)}`;
    };


    const formatTime = (date: string | number) => {
        if (diffMoreThanOneDay) {
            return dateTimeService.localizeShortTimeWithWeekday(date, user.cultureCode);
        }

        return dateTimeService.localizeShortTime(date, user.cultureCode);
    };

    const numToLocaleShortDate = (
        date: number | string
    ) => showHours
        ? formatTime(date)
        : formatDate(date);

    const responsiveContainerHeight = 320;

    return (
        <SalesChartComponent
            variables={{ dateFromTo: dateFromTo as DateFromTo }}
            fetchPolicy='network-only'
            Loader={() => <Placeholder minHeight={CHART_PLACEHOLDER_HEIGHT} />}
        >
            {({ data: { salesChartData } }) => {
                const chartData = generateChartData(salesChartData);
                const max = orderBy('total', 'desc', chartData)[0];
                return (
                    <Card fullHeight>
                        <CardContent padding={3}>
                            <Box display='flex' justifyContent='space-between'>
                                <CardTitle>{t('Revenue')}</CardTitle>
                                <DownloadCsvIconButton
                                    query={GET_MONEY_FLOW_REPORT}
                                    propertyKey='moneyFlowReport'
                                    fileName={t('money_flow_report')}
                                    variables={{ dateFromTo }}
                                />
                            </Box>
                            <ResponsiveContainer width="100%" height={responsiveContainerHeight}>
                                <AreaChart
                                    data={chartData}
                                    margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
                                >
                                    {max.total
                                        ? (
                                            <ReferenceLine y={max.total} stroke='#8993AA' strokeDasharray="6 6">
                                                <Label position='top'>
                                                    {priceService.toLocaleString(max.total)}
                                                </Label>
                                            </ReferenceLine>
                                        ) : null}
                                    <XAxis interval='preserveStartEnd' dataKey='date' tickFormatter={numToLocaleShortDate} />
                                    <CartesianAxis interval='preserveStartEnd' strokeDasharray="3 3" />
                                    <Tooltip labelFormatter={numToLocaleShortDate} formatter={formatChartTooltip} />
                                    <Area
                                        type="linear"
                                        dataKey='cash'
                                        stroke={theme.palette.primary.main}
                                        fill={theme.palette.primary.main}
                                        fillOpacity='0.16'
                                    />
                                    <Area
                                        type="linear"
                                        dataKey='creditCard'
                                        stroke={theme.palette.additional.purple}
                                        fill={theme.palette.additional.purple}
                                        fillOpacity='0.16'
                                    />
                                    <Area
                                        type="linear"
                                        dataKey='other'
                                        stroke={theme.palette.additional.orange}
                                        fill={theme.palette.additional.orange}
                                        fillOpacity='0.16'
                                    />
                                    <Area
                                        type="linear"
                                        dataKey='total'
                                        stroke={theme.palette.additional.green}
                                        fill={theme.palette.additional.green}
                                        fillOpacity='0.16'
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </CardContent>
                    </Card>
                );
            }}
        </SalesChartComponent>
    );
};

export default Chart;
